import type React from "react";
import { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import CommentInput from "../shared-ui/CommentInput";
import CustomTextField from "../../../../../../components/ui/Textfield";
import { tokens } from "../../../../../../theme";
import comments from "../../../../../../assets/comments.svg";
import { IStreamDetails } from "../types/memorial-types";
import type { CommentCardProps } from "../shared-ui/CommentCard";
import PhoneNumberField from "../../../../../../components/ui/PhoneNumberField";
import SuccessModal from "../shared-ui/SuccessModal";

export interface ICommentType {
  id: string;
  author: string;
  date: string;
  content: string;
  is_approved?: boolean;
  replies?: ICommentType[];
  parent_message?: string;
}

interface MemoriesTabProps {
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  isMobile: boolean;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  CommentCard: React.ComponentType<CommentCardProps>;
  colors: ReturnType<typeof tokens>;
  stream?: IStreamDetails;
  serviceType?: string;
  currentPage?: number;
  totalCount?: number;
  pageSize?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onMessageSent?: () => void;
  /** "standalone" (default) renders the Paper card + "Comments" heading used
   * by the regular funeral templates. "embedded" strips that outer chrome so
   * a parent (e.g. FuneralHomeTemplate's unified card) can supply its own —
   * all comment/reply behaviour is unchanged. */
  variant?: "standalone" | "embedded";
}

export default function MemoriesTab({
  messages,
  isAdmin,
  socketRef,
  isMobile,
  handleApprove,
  handleDelete,
  CommentCard,
  colors,
  stream: _stream,
  serviceType,
  currentPage,
  totalCount,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onMessageSent,
  variant = "standalone",
}: MemoriesTabProps) {
  const isEmbedded = variant === "embedded";
  void _stream;
  const [replyStates, setReplyStates] = useState<{
    [commentId: string]: boolean;
  }>({});
  const [replyTexts, setReplyTexts] = useState<{ [commentId: string]: string }>(
    {},
  );
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyFirstName, setReplyFirstName] = useState("");
  const [replySurname, setReplySurname] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyPhone, setReplyPhone] = useState("");
  const [replySaveDetails, setReplySaveDetails] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);


  const filterApprovedMessages = (msgs: ICommentType[]): ICommentType[] => {
    return msgs
      .filter((m) => m.is_approved)
      .map((m) => ({
        ...m,
        replies: m.replies ? filterApprovedMessages(m.replies) : [],
      }));
  };

  const handleReplyToggle = (id: string) => {
    setReplyStates((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleReplyChange = (id: string, value: string) => {
    setReplyTexts((prev) => ({ ...prev, [id]: value }));
  };

  // ✅ Fixed handleReplySubmit accepting authorName
  const handleReplySubmit = (
    parentId: string,
    replyText: string,
    authorName: string,
  ) => {
    if (!replyText.trim()) {
      showErrorToast("Reply cannot be empty.", "empty-reply-error");
      return;
    }

    const payload = {
      type: "message",
      message: replyText,
      sender_full_name: authorName,
      sender_email: replyEmail || "user@example.com",
      sender_phone_number: localStorage.getItem("commentPhone") || null,
      parent_message: parentId,
    };

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload));
      showSuccessToast("Reply sent successfully!");
      onMessageSent?.();
    } else {
      showErrorToast(
        "Failed to send reply. Please try again.",
        "reply-send-error",
      );
    }

    // Clear input and collapse reply
    setReplyTexts((prev) => ({ ...prev, [parentId]: "" }));
    setReplyStates((prev) => ({ ...prev, [parentId]: false }));
  };

  const handleReplySave = () => {
    if (!replyEmail || !replyMessage) {
      showErrorToast("Email and reply are required!", "reply-input-error");
      return;
    }

    if (replySaveDetails) {
      localStorage.setItem("commentEmail", replyEmail);
      localStorage.setItem("commentFullName", replyFirstName);
      localStorage.setItem("commentPhone", replyPhone);
    }

    const payload = {
      type: "message",
      message: replyMessage,
      first_name: replyFirstName,
      last_name: replySurname,
      sender_email: replyEmail,
      sender_phone_number: replyPhone,
      parent_message: null,
    };

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload));
      showSuccessToast("Reply sent successfully .. waiting for approval!");
      setSuccessOpen(true);
      onMessageSent?.();
    } else {
      showErrorToast(
        "Failed to send reply. Please try again.",
        "reply-send-error",
      );
    }

    setReplyMessage("");
    setReplyDialogOpen(false);
  };

  const visibleMessages = isAdmin ? messages : filterApprovedMessages(messages);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        component={isEmbedded ? "div" : Paper}
        sx={{
          ...(isEmbedded
            ? { position: "relative" }
            : {
                p: 2,
                boxShadow: 0,
                borderRadius: "20px",
                position: "relative",
                overflow: "visible",
              }),
        }}
      >
        {!isEmbedded && (
          <Typography
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: { xs: "1rem", sm: "0.7rem", md: "0.7rem" }, // responsive font size
            }}
          >
            Comments
          </Typography>
        )}

        {/* Reply Dialog */}
        <Dialog
          open={replyDialogOpen}
          onClose={() => setReplyDialogOpen(false)}
        >
          <DialogTitle>Enter Your Reply Details</DialogTitle>
          <DialogContent>
            <CustomTextField
              fullWidth
              margin="dense"
              label="First Name"
              value={replyFirstName}
              onChange={(e) => setReplyFirstName(e.target.value)}
            />
            <CustomTextField
              fullWidth
              margin="dense"
              label="Last Name"
              value={replySurname}
              onChange={(e) => setReplySurname(e.target.value)}
            />
            <CustomTextField
              fullWidth
              margin="dense"
              label="Email"
              value={replyEmail}
              onChange={(e) => setReplyEmail(e.target.value)}
              type="email"
              required
            />
            <PhoneNumberField
              fullWidth
              margin="dense"
              label="Phone Number"
              value={replyPhone}
              onChange={(value) => setReplyPhone(value)}
            />
            <CustomTextField
              fullWidth
              margin="dense"
              label="Your Reply"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              multiline
              rows={4}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={replySaveDetails}
                  onChange={(e) => setReplySaveDetails(e.target.checked)}
                />
              }
              label="Save my details for next time"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReplyDialogOpen(false)}
              sx={{ backgroundColor: colors.primary[800] }}
            >
              Cancel
            </Button>
            <Button onClick={handleReplySave} variant="contained">
              Send
            </Button>
          </DialogActions>
        </Dialog>

        {/* Messages */}
        <Box
          sx={{
            height: { xs: 370, sm: 370, md: 370 }, // responsive height
            overflowY: "auto",
            paddingBottom: 2,
          }}
        >
          {visibleMessages.length === 0 ? (
            <Box
              sx={{
                borderRadius: 4,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                backgroundColor: colors.primary[800],
                width: "100%",
              }}
            >
              {isEmbedded ? (
                <Typography color="text.secondary">
                  No comments yet. Be the first to comment!
                </Typography>
              ) : (
                <img
                  src={comments}
                  alt="no messages"
                  style={{
                    width: "100%",
                    maxWidth: 300,
                    height: "auto",
                    marginBottom: 16,
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: colors.primary[800],
                borderRadius: "16px",
                p: 2,
                height: "100%",
                overflowY: "auto",
              }}
            >
              <Stack spacing={2}>
                {visibleMessages.map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    isAdmin={isAdmin}
                    handleApprove={handleApprove}
                    handleDelete={handleDelete}
                    handleReplyToggle={handleReplyToggle}
                    replyStates={replyStates}
                    handleReplyChange={handleReplyChange}
                    replyTexts={replyTexts}
                    handleReplySubmit={handleReplySubmit}
                    colors={colors}
                    isMobile={isMobile}
                    messages={messages}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {typeof totalCount === "number" &&
          typeof currentPage === "number" &&
          typeof pageSize === "number" &&
          totalCount > 10 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  textAlign: { xs: "center", sm: "left" },
                  color: "text.secondary",
                }}
              >
                Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", sm: "row" },
                  justifyContent: { xs: "space-between", sm: "flex-end" },
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onPreviousPage}
                  disabled={!hasPreviousPage}
                  fullWidth={isMobile}
                  sx={{
                    minWidth: { xs: "48%", sm: 120 },
                    borderRadius: "12px",
                    textTransform: "none",
                  }}
                >
                  Previous
                </Button>

                <Button
                  variant="contained"
                  onClick={onNextPage}
                  disabled={!hasNextPage}
                  fullWidth={isMobile}
                  sx={{
                    minWidth: { xs: "48%", sm: 120 },
                    borderRadius: "12px",
                    textTransform: "none",
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

        <CommentInput
          socketRef={socketRef}
          isMobile={isMobile}
          serviceType={serviceType}
          onMessageSent={onMessageSent}
        />
      </Box>
       <SuccessModal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        title="Reply Sent Successfully!"
        message="Your reply has been submitted and is awaiting approval. It will appear once it has been reviewed."
      />
    </motion.div>
  );
}
